import { supabase } from '../lib/supabase';

/**
 * Document Service
 * Handles document upload, processing, and management for RAG chatbot
 */

/**
 * Upload a document to Supabase Storage
 * @param {File} file - The file to upload
 * @param {string} userId - The ID of the user uploading the file
 * @returns {Promise<Object>} Upload result with file URL and metadata
 */
export const uploadDocument = async (file, userId) => {
  try {
    // Validate file type
    const allowedTypes = ['application/pdf', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'text/plain'];
    if (!allowedTypes?.includes(file?.type)) {
      throw new Error('Invalid file type. Only PDF, DOCX, and TXT files are allowed.');
    }

    // Validate file size (max 10MB)
    const maxSize = 10 * 1024 * 1024; // 10MB in bytes
    if (file?.size > maxSize) {
      throw new Error('File size exceeds 10MB limit.');
    }

    // Generate unique filename
    const timestamp = Date.now();
    const fileExtension = file?.name?.split('.')?.pop();
    const fileName = `${timestamp}_${file?.name?.replace(/[^a-zA-Z0-9.-]/g, '_')}`;
    const filePath = `${fileName}`;

    // Upload to Supabase Storage
    const { data: uploadData, error: uploadError } = await supabase?.storage?.from('program_docs')?.upload(filePath, file, {
        cacheControl: '3600',
        upsert: false
      });

    if (uploadError) throw uploadError;

    // Get public URL
    const { data: urlData } = supabase?.storage?.from('program_docs')?.getPublicUrl(filePath);

    // Determine file type
    let fileType = 'txt';
    if (file?.type === 'application/pdf') {
      fileType = 'pdf';
    } else if (file?.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document') {
      fileType = 'docx';
    }

    // Insert document metadata
    const { data: docData, error: docError } = await supabase?.from('program_documents')?.insert({
        file_name: file?.name,
        file_url: uploadData?.path,
        file_type: fileType,
        file_size: file?.size,
        uploaded_by: userId,
        processing_status: 'pending'
      })?.select()?.single();

    if (docError) throw docError;

    // Trigger processing via Edge Function
    const { data: processData, error: processError } = await supabase?.functions?.invoke('process-document', {
      body: { docId: docData?.id }
    });

    if (processError) {
      console.error('Error triggering document processing:', processError);
      // Don't throw error here - document is uploaded, processing will happen async
    }

    return {
      success: true,
      document: docData,
      message: 'Document uploaded successfully. Processing will begin shortly.'
    };
  } catch (error) {
    console.error('Error uploading document:', error);
    throw error;
  }
};

/**
 * Get all program documents
 * @returns {Promise<Array>} List of documents
 */
export const getAllDocuments = async () => {
  try {
    const { data, error } = await supabase?.from('program_documents')?.select(`
        *,
        uploaded_by_profile:user_profiles(
          full_name,
          email,
          profile_image_url
        )
      `)?.order('created_at', { ascending: false });

    if (error) throw error;

    return data || [];
  } catch (error) {
    console.error('Error fetching documents:', error);
    throw error;
  }
};

/**
 * Delete a document and its chunks
 * @param {string} docId - Document ID
 * @returns {Promise<Object>} Delete result
 */
export const deleteDocument = async (docId) => {
  try {
    // Get document to find file path
    const { data: doc, error: fetchError } = await supabase?.from('program_documents')?.select('file_url')?.eq('id', docId)?.single();

    if (fetchError) throw fetchError;

    // Delete from storage
    const { error: storageError } = await supabase?.storage?.from('program_docs')?.remove([doc?.file_url]);

    if (storageError) {
      console.error('Error deleting from storage:', storageError);
      // Continue with database deletion even if storage fails
    }

    // Delete from database (chunks will be deleted via cascade)
    const { error: deleteError } = await supabase?.from('program_documents')?.delete()?.eq('id', docId);

    if (deleteError) throw deleteError;

    return {
      success: true,
      message: 'Document deleted successfully'
    };
  } catch (error) {
    console.error('Error deleting document:', error);
    throw error;
  }
};

/**
 * Reprocess a failed document
 * @param {string} docId - Document ID
 * @returns {Promise<Object>} Reprocess result
 */
export const reprocessDocument = async (docId) => {
  try {
    // Update status to pending
    const { error: updateError } = await supabase?.from('program_documents')?.update({
        processing_status: 'pending',
        error_message: null,
        chunk_count: 0
      })?.eq('id', docId);

    if (updateError) throw updateError;

    // Delete existing chunks
    const { error: deleteError } = await supabase?.from('doc_chunks')?.delete()?.eq('doc_id', docId);

    if (deleteError) console.error('Error deleting old chunks:', deleteError);

    // Trigger processing
    const { data: processData, error: processError } = await supabase?.functions?.invoke('process-document', {
      body: { docId }
    });

    if (processError) throw processError;

    return {
      success: true,
      message: 'Document reprocessing started'
    };
  } catch (error) {
    console.error('Error reprocessing document:', error);
    throw error;
  }
};

/**
 * Get document by ID with chunks
 * @param {string} docId - Document ID
 * @returns {Promise<Object>} Document with chunks
 */
export const getDocumentWithChunks = async (docId) => {
  try {
    const { data: doc, error: docError } = await supabase?.from('program_documents')?.select('*')?.eq('id', docId)?.single();

    if (docError) throw docError;

    const { data: chunks, error: chunksError } = await supabase?.from('doc_chunks')?.select('id, content, metadata, created_at')?.eq('doc_id', docId)?.order('metadata->chunk_index', { ascending: true });

    if (chunksError) throw chunksError;

    return {
      ...doc,
      chunks: chunks || []
    };
  } catch (error) {
    console.error('Error fetching document with chunks:', error);
    throw error;
  }
};

/**
 * Search documents using vector similarity
 * @param {string} query - Search query
 * @param {number} matchCount - Number of results to return
 * @returns {Promise<Array>} Matching chunks
 */
export const searchDocuments = async (query, matchCount = 5) => {
  try {
    // Generate embedding for query using Gemini
    const { data: embeddingData, error: embeddingError } = await supabase?.functions?.invoke('generate-embedding', {
      body: { text: query }
    });

    if (embeddingError) throw embeddingError;

    // Search using vector similarity
    const { data, error } = await supabase?.rpc('match_doc_chunks', {
      query_embedding: embeddingData?.embedding,
      match_threshold: 0.5,
      match_count: matchCount
    });

    if (error) throw error;

    return data || [];
  } catch (error) {
    console.error('Error searching documents:', error);
    throw error;
  }
};